import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Machine } from '@shared/entities/machine';
import { Paginate } from '@shared/entities/paginate';
import { ApiService, Pagination } from '@shared/services';
import { BehaviorSubject, Observable, Subject, combineLatest, interval, startWith, switchMap, take } from 'rxjs';
import { TIME_INTERVAL_REFRESH_MACHINES } from 'src/app/app.constants';

const MACHINES_PER_PAGE = 5;

export interface MachineFilterURL {
  state?: 'OK' | 'KO' | 'OFFLINE';
  serialNumber?: string;
  accountName?: string;
  typology?: string;
  location?: string;
  orderBy?: string;
  direction?: 'ASC' | 'DESC';
  locationId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MachineService extends ApiService<Machine> {
  private refresh$: Subject<Pagination>;
  all$: Observable<Paginate<Machine>>;
  currentFilters: MachineFilterURL = {};
  currentPaginator: Pagination;

  set filters(value: MachineFilterURL) {
    this.currentFilters = value;
    this.refresh(1, MACHINES_PER_PAGE);
  }

  constructor(private activatedRoute: ActivatedRoute) {
    const masterUrl = '/machines';
    const detailUrl = '/machine-details/{id}';

    super(Machine, masterUrl, detailUrl, 'items');

    this.currentPaginator = { page: 1, perPage: MACHINES_PER_PAGE };

    this.refresh$ = new BehaviorSubject<Pagination>(this.currentPaginator);

    this.all$ = combineLatest([interval(TIME_INTERVAL_REFRESH_MACHINES).pipe(startWith(0)), this.refresh$]).pipe(
      switchMap(([, pagination]) => {
        const { locationId } = this.activatedRoute.snapshot.queryParams;
        if (locationId) {
          this.setUrlParameter('locationId', locationId);
        }
        if (this.currentFilters.location) {
          this.setUrlParameter('location', this.currentFilters.location);
        }
        if (this.currentFilters.state) {
          this.setUrlParameter('state', this.currentFilters.state);
        }
        if (this.currentFilters.serialNumber) {
          this.setUrlParameter('serialNumber', this.currentFilters.serialNumber);
        }
        if (this.currentFilters.accountName) {
          this.setUrlParameter('accountName', this.currentFilters.accountName);
        }
        if (this.currentFilters.typology) {
          this.setUrlParameter('typology', this.currentFilters.typology);
        }
        if (this.currentFilters.orderBy) {
          this.setUrlParameter('order_by', this.currentFilters.orderBy);
          this.setUrlParameter('direction', this.currentFilters.direction ?? 'ASC');
        }
        return this.paginate(Machine, pagination.page, pagination.perPage);
      })
    );
  }

  refresh(page: number, perPage: number = MACHINES_PER_PAGE) {
    this.currentPaginator = { page, perPage };
    this.refresh$.next(this.currentPaginator);
    return this.all$.pipe(take(1));
  }
}
